import * as THREE from 'three';

export interface NPCConfig {
  scene: THREE.Scene;
  position: THREE.Vector3;
  id: string;
  name: string;
  type: 'quest_giver' | 'vendor' | 'trainer';
}

export class NPC {
  public mesh: THREE.Group;
  public id: string;
  public name: string;
  public type: string;
  public interactionRange: number = 3;

  private body: THREE.Mesh;
  private nameplate: THREE.Sprite;
  private questMarker: THREE.Mesh | null = null;

  // State
  private hasAvailableQuest: boolean = false;
  private hasCompletableQuest: boolean = false;

  constructor(config: NPCConfig) {
    this.id = config.id;
    this.name = config.name;
    this.type = config.type;

    this.mesh = new THREE.Group();

    // Create NPC body (friendly looking character)
    const bodyGeometry = new THREE.CapsuleGeometry(0.4, 1.2, 4, 8);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0x8b4513, // Brown robe
      roughness: 0.8,
      metalness: 0.1,
    });
    this.body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    this.body.position.y = 1.1;
    this.body.castShadow = true;
    this.mesh.add(this.body);

    // Head
    const headGeometry = new THREE.SphereGeometry(0.35, 12, 8);
    const headMaterial = new THREE.MeshStandardMaterial({
      color: 0xffdbac,
      roughness: 0.8,
    });
    const head = new THREE.Mesh(headGeometry, headMaterial);
    head.position.y = 2.1;
    head.castShadow = true;
    this.mesh.add(head);

    // Beard (for elder look)
    const beardGeometry = new THREE.ConeGeometry(0.25, 0.4, 6);
    const beardMaterial = new THREE.MeshStandardMaterial({
      color: 0xcccccc,
      roughness: 0.9,
    });
    const beard = new THREE.Mesh(beardGeometry, beardMaterial);
    beard.position.set(0, 1.85, 0.2);
    beard.rotation.x = Math.PI;
    this.mesh.add(beard);

    // Staff
    const staffGeometry = new THREE.CylinderGeometry(0.05, 0.05, 2.5, 6);
    const staffMaterial = new THREE.MeshStandardMaterial({
      color: 0x4a3728,
    });
    const staff = new THREE.Mesh(staffGeometry, staffMaterial);
    staff.position.set(0.6, 1.25, 0);
    staff.rotation.z = 0.1;
    this.mesh.add(staff);

    // Staff orb
    const orbGeometry = new THREE.SphereGeometry(0.15, 8, 6);
    const orbMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
    });
    const orb = new THREE.Mesh(orbGeometry, orbMaterial);
    orb.position.set(0.7, 2.5, 0);
    this.mesh.add(orb);

    // Nameplate (billboard text sprite)
    this.nameplate = this.createNameplate();
    this.mesh.add(this.nameplate);

    // Quest marker (initially hidden)
    this.questMarker = this.createQuestMarker();
    this.mesh.add(this.questMarker);
    this.questMarker.visible = false;

    // Set position
    this.mesh.position.copy(config.position);

    // Add to scene
    config.scene.add(this.mesh);
  }

  private createNameplate(): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = '#00ff00';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.name, 128, 32);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
    });
    const sprite = new THREE.Sprite(material);
    sprite.position.y = 2.8;
    sprite.scale.set(2, 0.5, 1);

    return sprite;
  }

  private createQuestMarker(): THREE.Mesh {
    // Yellow exclamation mark / question mark for quests
    const geometry = new THREE.ConeGeometry(0.2, 0.5, 4);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffff00,
    });
    const marker = new THREE.Mesh(geometry, material);
    marker.position.y = 3.2;
    marker.rotation.x = Math.PI; // Point up

    return marker;
  }

  public update(deltaTime: number): void {
    // Animate quest marker
    if (this.questMarker && this.questMarker.visible) {
      this.questMarker.rotation.y += deltaTime * 2;
      this.questMarker.position.y = 3.2 + Math.sin(Date.now() * 0.003) * 0.1;
    }
  }

  public setQuestState(hasAvailable: boolean, hasCompletable: boolean): void {
    this.hasAvailableQuest = hasAvailable;
    this.hasCompletableQuest = hasCompletable;

    if (this.questMarker) {
      this.questMarker.visible = hasAvailable || hasCompletable;

      // Yellow for available, gold/orange for completable
      const material = this.questMarker.material as THREE.MeshBasicMaterial;
      if (hasCompletable) {
        material.color.setHex(0xffd700); // Gold - ready to turn in
      } else if (hasAvailable) {
        material.color.setHex(0xffff00); // Yellow - new quest
      }
    }
  }

  public isInRange(playerPosition: THREE.Vector3): boolean {
    const distance = this.mesh.position.distanceTo(playerPosition);
    return distance <= this.interactionRange;
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public facePosition(position: THREE.Vector3): void {
    const direction = new THREE.Vector3()
      .subVectors(position, this.mesh.position)
      .normalize();
    const angle = Math.atan2(direction.x, direction.z);
    this.mesh.rotation.y = angle;
  }

  public destroy(): void {
    this.mesh.parent?.remove(this.mesh);
  }
}
